import {
  Directive,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  SimpleChanges,
  Renderer2
} from '@angular/core';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[srCollapsible]',
})
export class CollapsibleDirective
  implements AfterViewInit, OnChanges, OnDestroy {

  // -----------------------------
  // Inputs
  // -----------------------------
  @Input() srCollapsible: { accordion?: boolean } = { accordion: true };

  // Single open index (accordion mode)
  @Input() srCollapsibleOpenIndex: number | null = null;

  @Input() appCollapsibleActions: EventEmitter<string> | null = null;

  @Input() set appCollapsibleRefresh(val: number) {
    if (!this._initialized) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();
      queueMicrotask(() => this.init(false));
    });

    observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: false
    });

    setTimeout(() => this.init(false), 50);
  }

  // -----------------------------
  // Outputs
  // -----------------------------
  @Output() opened = new EventEmitter<number>();
  @Output() closed = new EventEmitter<number>();
  @Output() headerClicked = new EventEmitter<number>();

  // -----------------------------
  // Internal state
  // -----------------------------
  private items: HTMLElement[] = [];
  private accordion = true;
  private listeners: Array<() => void> = [];
  // Track open/closed state
  private openStates: boolean[] = [];
  private _initialized = false;
  private actionsSub: Subscription | null = null;
  private persistentObserver: MutationObserver | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) { }

  ngAfterViewInit(): void {
    this._initialized = true;

    // Run once immediately if items already exist
    queueMicrotask(() => {
      const itemCount = Array.from(this.el.nativeElement.children)
        .filter(c => c.tagName === 'LI').length;
      if (itemCount > 0) {
        this.init();
      }
    });

    // PERSISTENT observer — watches for the entire lifetime of the directive,
    // re-attaching listeners any time *ngFor recreates the <li> items
    // (e.g. parent passes a new array reference on modal switch)
    this.persistentObserver = new MutationObserver(() => {
      queueMicrotask(() => this.init(false));
    });

    this.persistentObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: false
    });

    if (this.appCollapsibleActions) {
      this.actionsSub = this.appCollapsibleActions.subscribe((action) => {
        this.handleAction(action);
      });
    }
  }

  ngAfterViewInit1(): void {
    this._initialized = true;

    queueMicrotask(() => {
      const itemCount = Array.from(this.el.nativeElement.children)
        .filter(c => c.tagName === 'LI').length;
      if (itemCount > 0) {
        this.init();
      } else {
        const observer = new MutationObserver(() => {
          const count = Array.from(this.el.nativeElement.children)
            .filter(c => c.tagName === 'LI').length;

          if (count > 0) {
            observer.disconnect();
            queueMicrotask(() => {
              this.init();
            });
          }
        });

        observer.observe(this.el.nativeElement, {
          childList: true,
          subtree: false
        });
      }
    });
    // Subscribe to the actions emitter, if provided
    if (this.appCollapsibleActions) {
      this.actionsSub = this.appCollapsibleActions.subscribe((action) => {
        this.handleAction(action);
      });
    }
  }

  private handleAction(action: string): void {
    switch (action) {
      case 'collapsible':
      case 'refresh':
        this.init(false);
        break;
      case 'close':
      case 'destroy':
        this.closeAll();
        break;
      default:
        console.warn('[CollapsibleDirective] unknown action:', action);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['srCollapsibleOpenIndex'] &&
      !changes['srCollapsibleOpenIndex'].firstChange
    ) {
      this.applyOpenIndex(this.srCollapsibleOpenIndex);
    }
  }

  ngOnDestroy(): void {
    this.persistentObserver?.disconnect();
    this.actionsSub?.unsubscribe();
    this.cleanupListeners();
  }

  openPanel(index: number): void {
    this.applyOpenIndex(index);
  }

  closePanel(index: number): void {
    if (!this.items[index]) return;
    const li = this.items[index];
    const body = li.querySelector('.collapsible-body') as HTMLElement;

    if (body) {
      this.renderer.setStyle(body, 'display', 'none');
      li.classList.remove('active');
      this.openStates[index] = false;
      this.closed.emit(index);
    }
  }

  togglePanel(index: number): void {
    if (!this.items[index]) return;
    this.toggleItem(this.items[index]);
  }

  // -----------------------------
  // Initialization
  // -----------------------------

  private init(autoOpen = true): void {
    this.cleanupListeners();

    const root = this.el.nativeElement;
    this.accordion = this.srCollapsible.accordion ?? true;

    const previousOpenStates = [...this.openStates];
    const previousItems = [...this.items];

    this.items = Array.from(root.children).filter(
      (child) => child.tagName === 'LI'
    ) as HTMLElement[];

    this.openStates = this.items.map(() => false);

    // Hide all bodies AND reset active class
    this.items.forEach((li) => {
      li.classList.remove('active');
      const body = li.querySelector('.collapsible-body') as HTMLElement;
      if (body) {
        this.renderer.setStyle(body, 'display', 'none');
        this.renderer.addClass(body, 'collapsible-body');
      }
    });

    // Only auto-open if explicitly allowed
    if (autoOpen && this.srCollapsibleOpenIndex != null) {
      this.applyOpenIndex(this.srCollapsibleOpenIndex);
    } else if (!autoOpen) {
      let restored = false;  // accordion guard
      // Restore previously open items where they still exist
      this.items.forEach((li, newIndex) => {
        // Match by DOM reference to handle deletions correctly
        const oldIndex = previousItems.indexOf(li);
        if (oldIndex !== -1 && previousOpenStates[oldIndex]) {
          const body = li.querySelector('.collapsible-body') as HTMLElement;
          if (body) {
            this.renderer.setStyle(body, 'display', 'block');
            li.classList.add('active');
            this.openStates[newIndex] = true;
            restored = true;
          }
        }
      });
    }

    this.attachListeners();
  }

  private cleanupListeners(): void {
    this.listeners.forEach((unlisten) => unlisten());
    this.listeners = [];
  }

  // -----------------------------
  // Attach listeners
  // -----------------------------
  private attachListeners(): void {
    this.items.forEach((li) => {
      const header = li.querySelector('.collapsible-header');
      if (!header) return;

      const unlisten = this.renderer.listen(header, 'click', () => {
        const index = this.items.indexOf(li);
        this.headerClicked.emit(index);
        this.toggleItem(li);
      });

      this.listeners.push(unlisten);
    });
  }

  // -----------------------------
  // Toggle Logic
  // -----------------------------
  private applyOpenIndex(index: number | null): void {
    if (index == null || index < 0 || !this.items.length) return;

    const idx = Math.min(index, this.items.length - 1);

    if (this.accordion) {
      // Close all others
      this.items.forEach((li, i) => {

        const body = li.querySelector('.collapsible-body') as HTMLElement;
        if (!body) return;

        const isTarget = i === idx;

        this.renderer.setStyle(body, 'display', isTarget ? 'block' : 'none');
        if (isTarget) {
          li.classList.add('active');   // ensure chevron points up
          this.openStates[i] = true;
          this.opened.emit(i);
        } else {
          li.classList.remove('active'); // ensure chevron resets
          this.openStates[i] = false;
          this.closed.emit(i);
        }
      });
    }
  }

  private toggleItem(clickedLi: HTMLElement): void {
    const index = this.items.indexOf(clickedLi);
    if (index === -1) return;

    const body = clickedLi.querySelector('.collapsible-body') as HTMLElement;
    if (!body) return;

    const isOpen = this.openStates[index];

    if (this.accordion) {
      // Close all others
      this.items.forEach((li, i) => {
        if (i !== index && this.openStates[i]) {
          const b = li.querySelector('.collapsible-body') as HTMLElement;
          this.renderer.setStyle(b, 'display', 'none');
          li.classList.remove('active');
          this.openStates[i] = false;
          this.closed.emit(i);
        }
      });
    }

    // Toggle clicked item
    if (isOpen) {
      // CLOSE
      this.renderer.setStyle(body, 'display', 'none');
      clickedLi.classList.remove('active');
      this.openStates[index] = false;
      this.closed.emit(index);
    } else {
      // OPEN
      this.renderer.setStyle(body, 'display', 'block');
      clickedLi.classList.add('active');
      this.openStates[index] = true;
      this.opened.emit(index);
    }
  }

  closeAll(): void {
    this.items.forEach((li, i) => {
      li.classList.remove('active');
      const body = li.querySelector('.collapsible-body') as HTMLElement;
      if (body) {
        this.renderer.setStyle(body, 'display', 'none');
      }
      this.openStates[i] = false;
    });
  }

}
